import type { Catalog, CatalogEntry } from '@tales/story-schema';
import { CONTENT_BASE } from '../lib/content';
import { useSession } from '../lib/session';

interface Props {
  catalog: Catalog;
  onOpenSeries: (seriesId: string) => void;
  onPlay: (seriesId: string, episodeId: string) => void;
}

function Hero({ s, onOpenSeries, onPlay }: { s: CatalogEntry } & Props) {
  const first = s.episodes[0];
  return (
    <div className="hero" onClick={() => onOpenSeries(s.id)}>
      {s.cover ? <img src={`${CONTENT_BASE}/${s.id}/${s.cover}`} alt="" /> : null}
      <div className="meta">
        <div className="chips">
          {s.genres.map((g) => (
            <span className="chip" key={g}>
              {g}
            </span>
          ))}
          <span className="chip">interactive</span>
        </div>
        <h2>{s.title}</h2>
        {s.tagline ? <p>{s.tagline}</p> : null}
        {first ? (
          <button
            className="cta"
            onClick={(e) => {
              e.stopPropagation();
              onPlay(s.id, first.id);
            }}
          >
            ▶ Play episode 1
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Card({ s, episodeId, label, sub, pct, onClick }: { s: CatalogEntry; episodeId?: string; label: string; sub?: string; pct?: number; onClick: () => void }) {
  const img = episodeId ? `${CONTENT_BASE}/${s.id}/${episodeId}/media/n01.jpg` : s.cover ? `${CONTENT_BASE}/${s.id}/${s.cover}` : '';
  return (
    <a
      className="card"
      href={episodeId ? `/play/${s.id}/${episodeId}` : `/s/${s.id}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <img src={img} alt="" />
      <div className="t">{label}</div>
      {sub ? <div className="s">{sub}</div> : null}
      {pct !== undefined ? (
        <div className="bar">
          <div style={{ width: `${Math.round(pct * 100)}%` }} />
        </div>
      ) : null}
    </a>
  );
}

export function Home(props: Props) {
  const { catalog, onOpenSeries, onPlay } = props;
  const { progress, user, online, ready } = useSession();
  const featured = catalog.series[0];
  const byId = new Map(catalog.series.map((s) => [s.id, s]));
  const continueWatching = progress
    .filter((p) => !p.completedAt && byId.has(p.seriesId))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 10);
  const genres = [...new Set(catalog.series.flatMap((s) => s.genres))];
  return (
    <div className="home">
      <header>
        <h1>Tales</h1>
        <span className="label">stories you steer</span>
        {user ? <span className="wallet">{user.credits}</span> : null}
      </header>
      {featured ? <Hero s={featured} {...props} /> : <p style={{ padding: 20 }}>No stories yet.</p>}
      {ready && !online ? <p style={{ padding: '0 20px', fontSize: 12.5, color: 'var(--muted)' }}>Offline mode: free episodes only until the service is reachable.</p> : null}
      <div className="home-rows">
        {continueWatching.length ? (
          <section>
            <h3 className="row">Continue watching</h3>
            <div className="cards">
              {continueWatching.map((p) => {
                const s = byId.get(p.seriesId)!;
                const ep = s.episodes.find((e) => e.id === p.episodeId);
                const idx = s.episodes.findIndex((e) => e.id === p.episodeId);
                return <Card key={`${p.seriesId}/${p.episodeId}`} s={s} episodeId={p.episodeId} label={s.title} sub={`Ep ${idx + 1} · ${ep?.title ?? ''}`} pct={Math.min(0.9, p.steps / 6)} onClick={() => onPlay(s.id, p.episodeId)} />;
              })}
            </div>
          </section>
        ) : null}
        <section>
          <h3 className="row">Trending now</h3>
          <div className="cards">
            {catalog.series.map((s) => (
              <Card key={s.id} s={s} label={s.title} sub={`${s.episodes.length} episodes`} onClick={() => onOpenSeries(s.id)} />
            ))}
          </div>
        </section>
        <section>
          <h3 className="row">New episodes</h3>
          <div className="cards">
            {catalog.series.flatMap((s) =>
              s.episodes
                .slice(-2)
                .reverse()
                .map((e) => <Card key={`${s.id}/${e.id}`} s={s} episodeId={e.id} label={e.title} sub={`${s.title} · ${e.price ? `${e.price} ◈` : 'Free'}`} onClick={() => onPlay(s.id, e.id)} />),
            )}
          </div>
        </section>
        {genres.map((g) => (
          <section key={g}>
            <h3 className="row">{g[0]!.toUpperCase() + g.slice(1)}</h3>
            <div className="cards">
              {catalog.series
                .filter((s) => s.genres.includes(g))
                .map((s) => (
                  <Card key={s.id} s={s} label={s.title} sub={s.tagline ?? ''} onClick={() => onOpenSeries(s.id)} />
                ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
